/**
 * Where tapping a notification goes — one place for the notifications list,
 * the in-app banner and a tapped push. clickAction is "target" or
 * "target:id" (ids may contain ':' — facebook_post:pageId:postId).
 */
import type { Screen } from '../contexts/AppContext';

type NavigateTo = (screen: Screen, param?: string, routeParams?: Record<string, any>) => void;

/** Plain screens a notification may point at directly. */
const DIRECT: Partial<Record<string, Screen>> = {
  chat: 'chat',
  calendars: 'calendars',
  subscription: 'subscription',
  'user-setup': 'user-setup',
  inventory: 'inventory',
  'call-history': 'call-history',
  quotations: 'quotations',
  orders: 'orders',
  'whatsapp-business-setup': 'whatsapp-business-setup',
  'facebook-messenger-setup': 'facebook-messenger-setup',
  'x-twitter-setup': 'x-twitter-setup',
  'phone-setup': 'phone-setup',
  'posts-list': 'posts-list',
  'task-history': 'task-history',
  home: 'home',
  'lead-finder': 'lead-finder',
  'assignment-rules': 'assignment-rules',
};

export async function openNotificationTarget(
  navigateTo: NavigateTo,
  clickAction?: string | null,
  data?: Record<string, any> | null
): Promise<void> {
  if (!clickAction) return;
  const [target, ...rest] = clickAction.split(':');
  const id = rest.length ? rest.join(':') : null;

  switch (target) {
    case 'conversation':
      if (id) navigateTo('conversation', id);
      else navigateTo('chat');
      return;
    case 'contact': {
      // A contact's conversation (on its channel), else the contacts list
      try {
        const { apiService } = await import('../services/api');
        const res: any = await apiService.authenticatedRequest('/api/omni-channel/conversations/findOrCreate', {
          method: 'POST',
          body: JSON.stringify({ contactId: data?.contactId || id, channel: data?.channel || 'whatsapp' }),
        });
        const conversationId = res?.data?.conversationId;
        if (conversationId) {
          navigateTo('conversation', conversationId);
          return;
        }
      } catch {
        // fall through
      }
      navigateTo('contacts');
      return;
    }
    case 'task':
      if (id) {
        const { setPendingTaskId } = await import('../utils/tempStorage');
        await setPendingTaskId(id);
      }
      navigateTo('task-history');
      return;
    case 'facebook_post': {
      const [pageId, postId] = (id || '').split(':');
      navigateTo('posts-list', undefined, { pageId, postId, status: 'published' });
      return;
    }
    case 'order':
      if (id) navigateTo('order-edit', id);
      else navigateTo('orders');
      return;
    case 'quotation':
      if (id) navigateTo('quotation-detail', id);
      else navigateTo('quotations');
      return;
    case 'visit':
      if (id) navigateTo('visit-detail', undefined, { visitId: id });
      else navigateTo('visits');
      return;
    case 'event':
      if (id) navigateTo('event-detail', id);
      else navigateTo('business-calendar');
      return;
    case 'assignment':
      navigateTo('contacts');
      return;
    case 'data-list':
      navigateTo('data-lists');
      return;
    case 'lead-run':
      if (id) navigateTo('lead-run', id);
      else navigateTo('lead-finder');
      return;
    case 'settings': {
      if (id) {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(id);
      }
      navigateTo('settings');
      return;
    }
    default: {
      const screen = DIRECT[target];
      navigateTo(screen || 'home');
    }
  }
}
